import React, { useState, useEffect, useRef } from "react";
import { ChevronDown, MoreVerticalIcon } from "lucide-react";
import EditVideoModal from "../Modals/EditVideoModal";
import PublishVideoModal from "../Modals/PublishVideoModal";
import DeleteVideoModal from "../Modals/DeleteVideoModal";
import { getInspirationalData, deleteInspirationalVideo, toggleInspirationalStatus } from "../../requests";
import { useDispatch } from "react-redux";
import { startLoader, stopLoader } from "../../_store/reducers/loaderReducer.js";
import { notifySuccess, notifyError } from "../../utils/helper";
import styles from "../../_css/ContenetManagement.module.css";
import WatchVideoModal from "../Modals/WatchVideoModal";

const InspirationalCorner = ({ dataFetch }) => {
    const dispatch = useDispatch();
    const [inspirationalData, setInspirationalData] = useState([]);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showPublishModal, setShowPublishModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showSortDropdown, setSortDropdown] = useState(false);
    const [showPublishedDropdown, setShowPublishedDropdown] = useState(false);
    const [menuOpenIndex, setMenuOpenIndex] = useState(null);
    const [activeModal, setActiveModal] = useState({ modal: null, index: null });
    const [showWatchModal, setShowWatchModal] = useState(false);
    const [currentVideo, setCurrentVideo] = useState(null);

    const [filters, setFilters] = useState({
        status: "All",
        sortBy: "Latest First"
    });

    const sortDropdownRef = useRef(null);
    const publishedDropdownRef = useRef(null);
    const menuRefs = useRef([]);

    // Fetch data function
    const fetchInspirationalData = async () => {
        try {
            dispatch(startLoader());
            const response = await getInspirationalData();
            if (response && response.data) {
                setInspirationalData(response.data);
            }
        } catch (error) {
            notifyError("Failed to fetch videos");
        } finally {
            dispatch(stopLoader());
        }
    };

    useEffect(() => {
        fetchInspirationalData();
    }, [dispatch]);

    useEffect(() => {
        fetchInspirationalData();
    }, [dataFetch]);

    const dataToUse = inspirationalData.length > 0 ? inspirationalData : [];

    const filteredAndSortedData = dataToUse
        .filter(item => {
            if (filters.status === "All") return true;
            return item.status === filters.status;
        })
        .sort((a, b) => {
            if (filters.sortBy === "Latest First") {
                return new Date(b.createdAt) - new Date(a.createdAt);
            } else {
                return new Date(a.createdAt) - new Date(b.createdAt);
            }
        });

    // Click outside handler for dropdowns
    useEffect(() => {
        const handleClickOutside = (e) => {
            // Check if we're clicking inside a menu trigger or dropdown
            const menuTrigger = e.target.closest('[data-menu-trigger]');
            const dropdownMenu = e.target.closest('[data-dropdown-menu]');

            if (!menuTrigger && !dropdownMenu) {
                setMenuOpenIndex(null);
            }

            if (sortDropdownRef.current && !sortDropdownRef.current.contains(e.target)) {
                setSortDropdown(false);
            }
            if (publishedDropdownRef.current && !publishedDropdownRef.current.contains(e.target)) {
                setShowPublishedDropdown(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleToggleStatus = async (video) => {
        try {
            dispatch(startLoader());
            const response = await toggleInspirationalStatus(video.id, video.status);
            // Refetch data to ensure sync
            await fetchInspirationalData();
            notifySuccess(response.message);
        } catch (error) {
            notifyError("Failed to update status");
        } finally {
            dispatch(stopLoader());
        }
    };

    const handleWatchNow = (videoData) => {
        setCurrentVideo(videoData);
        setShowWatchModal(true);
    };

    const toggleSortDropdown = () => {
        setSortDropdown(!showSortDropdown);
        setShowPublishedDropdown(false);
    };

    const togglePublishedDropdown = () => {
        setShowPublishedDropdown(!showPublishedDropdown);
        setSortDropdown(false);
    };

    const handleDeleteVideo = async (videoId) => {
        if (!videoId) return;
        try {
            dispatch(startLoader());
            await deleteInspirationalVideo(videoId);
            // Refetch data to ensure sync
            await fetchInspirationalData();
            notifySuccess("Video deleted successfully!");
            handleModalClose();
        } catch (error) {
            notifyError(error.message || "Failed to delete video");
        } finally {
            dispatch(stopLoader());
        }
    };

    const toggleMenu = (index, e) => {
        if (e) e.stopPropagation();
        setMenuOpenIndex(menuOpenIndex === index ? null : index);
        setSortDropdown(false);
        setShowPublishedDropdown(false);
    };

    const handleSortSelect = (sortOption) => {
        setFilters(prev => ({ ...prev, sortBy: sortOption }));
        setSortDropdown(false);
    };

    const handleStatusSelect = (statusOption) => {
        setFilters(prev => ({ ...prev, status: statusOption }));
        setShowPublishedDropdown(false);
    };

    const handleMenuAction = (item, index, e) => {
        if (e) e.stopPropagation();

        const videoData = filteredAndSortedData[index];

        if (!videoData) {
            return;
        }

        const originalIndex = dataToUse.findIndex(dataItem => dataItem.id === videoData.id);

        setActiveModal({ modal: item.toLowerCase(), index: originalIndex });
        setMenuOpenIndex(null);

        if (item.toLowerCase() === 'edit') {
            setShowEditModal(true);
        } else if (item.toLowerCase() === 'unpublish' || item.toLowerCase() === 'publish') {
            setShowPublishModal(true);
        } else if (item.toLowerCase() === 'delete') {
            setShowDeleteModal(true);
        }
    };

    const getCurrentVideoData = () => {
        if (activeModal.index === null || activeModal.index === undefined) return null;
        return filteredAndSortedData[activeModal.index];
    };

    const handleModalClose = () => {
        setShowEditModal(false);
        setShowPublishModal(false);
        setShowDeleteModal(false);
        setShowWatchModal(false);
        setActiveModal({ modal: null, index: null });
        setCurrentVideo(null);
    };

    // Handle successful update from EditVideoModal
    const handleVideoUpdate = async () => {
        // Refetch data to ensure sync after edit
        await fetchInspirationalData();
        handleModalClose();
    };

    const getMenuOptions = (status) => {
        if (status === 'Publish') {
            return ['Unpublish', 'Edit', 'Delete'];
        } else {
            return ['Publish', 'Edit', 'Delete'];
        }
    };

    const currentVideoData = getCurrentVideoData();

    return (
        <div className={styles.container}>
            {/* Filters */}
            <div className={styles.filtersContainer}>
                <div className={styles.filterDropdownContainer} ref={sortDropdownRef}>
                    <button
                        onClick={toggleSortDropdown}
                        className={styles.filterButton}
                        data-menu-trigger
                    >
                        {filters.sortBy} <ChevronDown size={16} color="#6b7280" />
                    </button>

                    {showSortDropdown && (
                        <div className={styles.dropdown} data-dropdown-menu>
                            {["Latest First", "Oldest First"].map((item, idx) => (
                                <div
                                    key={idx}
                                    onClick={() => handleSortSelect(item)}
                                    className={`${styles.dropdownItem} ${filters.sortBy === item ? styles.dropdownItemActive : styles.dropdownItemInactive
                                        }`}
                                >
                                    {item}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className={styles.filterDropdownContainer} ref={publishedDropdownRef}>
                    <button
                        onClick={togglePublishedDropdown}
                        className={styles.filterButton}
                        data-menu-trigger
                    >
                        {filters.status} <ChevronDown size={16} color="#6b7280" />
                    </button>

                    {showPublishedDropdown && (
                        <div className={styles.dropdown} data-dropdown-menu>
                            {["All", "Publish", "Draft"].map((item, idx) => (
                                <div
                                    key={idx}
                                    onClick={() => handleStatusSelect(item)}
                                    className={`${styles.dropdownItem} ${filters.status === item ? styles.dropdownItemActive : styles.dropdownItemInactive
                                        }`}
                                >
                                    {item}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <div className={styles.grid}>
                {filteredAndSortedData.map((data, index) => (
                    <div key={data.id} className={styles.Inspirationalcard}>
                        <div className={styles.imageContainer}>
                            <div className={`${styles.statusBadge} ${data.status.includes('Publish') ? styles.statusPublished : styles.statusDraft
                                }`}>
                                {data.status.includes('Publish') ? 'Published' : 'Draft'}
                            </div>

                            <div
                                ref={el => menuRefs.current[index] = el}
                                className={styles.menuButton}
                                onClick={(e) => toggleMenu(index, e)}
                                data-menu-trigger
                            >
                                <MoreVerticalIcon size={20} color='#FFFFFF' />
                            </div>

                            {menuOpenIndex === index && (
                                <div className={styles.menuDropdown} data-dropdown-menu>
                                    {getMenuOptions(data.status).map((item, idx) => (
                                        <div
                                            key={idx}
                                            className={styles.menuItem}
                                            onClick={(e) => handleMenuAction(item, index, e)}
                                        >
                                            {item}
                                        </div>
                                    ))}
                                </div>
                            )}

                            <img src={data.imageUrl} alt={data.heading} className={styles.image} />
                        </div>

                        <div className={styles.content}>
                            <span className={styles.category}>
                                {data.heading}
                            </span>
                            <h3 className={styles.title}>
                                {data.subHeading}
                            </h3>
                            <p className={styles.speaker}>
                                By {data.speaker}
                            </p>
                            <div className={styles.footer}>
                                <span className={styles.duration}>
                                    {data.duration} Duration
                                </span>
                                <button
                                    className={styles.watchButton}
                                    onClick={() => handleWatchNow(data)}
                                >
                                    <span>Watch now</span>
                                    <span className={styles.playIcon}>▶</span>
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {filteredAndSortedData.length === 0 && (
                <div className={styles.emptyState}>
                    No videos found matching the selected filters.
                </div>
            )}

            {/* Modals */}
            {showEditModal && (
                <EditVideoModal
                    show={showEditModal}
                    onHide={handleModalClose}
                    videoData={currentVideoData}
                    onUpdate={handleVideoUpdate}
                />
            )}

            {showPublishModal && (
                <PublishVideoModal
                    show={showPublishModal}
                    onHide={handleModalClose}
                    videoData={currentVideoData}
                    action={activeModal.modal}
                    onConfirm={async () => {
                        if (!currentVideoData) return;
                        try {
                            dispatch(startLoader());
                            const response = await toggleInspirationalStatus(currentVideoData.id, currentVideoData.status);
                            // Refetch data to ensure sync
                            await fetchInspirationalData();
                            notifySuccess(response.message);
                            handleModalClose();
                        } catch (error) {
                            notifyError("Failed to update status");
                        } finally {
                            dispatch(stopLoader());
                        }
                    }}
                />
            )}

            {showDeleteModal && (
                <DeleteVideoModal
                    show={showDeleteModal}
                    onHide={handleModalClose}
                    videoData={currentVideoData}
                    onDelete={() => handleDeleteVideo(currentVideoData.id)}
                />
            )}
            <WatchVideoModal
                show={showWatchModal}
                onHide={handleModalClose}
                videoData={currentVideo}
            />
        </div>
    );
};

export default InspirationalCorner;